import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type { GloryShopItemDef } from '../../../master-data/types/glory-shop.types.ts';

// glory_shop_item — 글로리 샵 상품 카탈로그.
// cost / delivery_items 는 JSONB 이므로 kind:'json' 으로 통째 보존한다.
export const GloryShopItemSchema = defineSchema({
  name: 'serverGloryShopItems',
  fields: [
    ['id', 'string'],
    ['nameKey', 'string'],
    ['category', 'string'],
    ['displayOrder', 'number'],
    ['cost', { kind: 'json' }],
    ['deliveryItems', { kind: 'json' }],
    ['purchaseLimit', 'number?'],
    ['periodLimitCount', 'number?'],
    ['periodLimitDays', 'number?'],
    ['startAt', 'number?'],
    ['endAt', 'number?'],
    ['icon', 'string?'],
    ['isActive', 'boolean'],
    // append-only (튜플 와이어 순서 계약) — 신규 필드는 항상 끝에 붙인다.
    ['saleSeasonId', 'number?'],
    ['gachaBoxId', 'string?'],
    ['gachaTenCost', 'number?'],
  ],
  lookupKeys: ['id'],
});

export interface RawGloryShopItemRow {
  id: string;
  name_key: string;
  category: string | null;
  display_order: number | null;
  cost: Record<string, number> | null;
  delivery_items: Record<string, number> | null;
  purchase_limit: number | null;
  period_limit: string | null;
  start_at: number | null;
  end_at: number | null;
  sale_season_id: number | null;
  icon: string | null;
  is_active: boolean;
  gacha_box_id: string | null;
  gacha_ten_cost: number | null;
}

const PERIOD_DAYS: Record<string, number> = { day: 1, week: 7, month: 30 };

export function gloryShopItemFromRaw(row: RawGloryShopItemRow): GloryShopItemDef {
  const def: GloryShopItemDef = {
    id: String(row.id),
    nameKey: String(row.name_key),
    category: (row.category ?? 'CURRENCY') || 'CURRENCY',
    displayOrder: Number(row.display_order ?? 0),
    cost: row.cost ?? {},
    deliveryItems: row.delivery_items ?? {},
    isActive: Boolean(row.is_active),
  };
  if (row.purchase_limit != null && row.purchase_limit > 0) {
    def.purchaseLimit = Number(row.purchase_limit);
  }
  const periodRaw = String(row.period_limit ?? '').trim();
  if (periodRaw) {
    const [countRaw, unitRaw] = periodRaw.split('/');
    const days = PERIOD_DAYS[(unitRaw ?? '').toLowerCase()];
    const count = Number.parseInt(countRaw ?? '', 10);
    if (days != null && Number.isFinite(count) && count > 0) {
      def.periodLimitCount = count;
      def.periodLimitDays = days;
    }
  }
  if (row.start_at != null) def.startAt = Number(row.start_at);
  if (row.end_at != null) def.endAt = Number(row.end_at);
  // 시즌 id 는 정수 양수만 유효하다. 0/음수/NaN 을 그대로 흘리면 판정부가 "시즌 못 찾음"
  // 으로 fail-closed 하긴 하지만, 여기서 걸러야 authoring 오류가 상품 실종으로만
  // 나타나지 않는다(운영자가 컬럼 값을 보고 원인을 짚을 수 있다).
  if (row.sale_season_id != null) {
    const seasonId = Number(row.sale_season_id);
    if (Number.isInteger(seasonId) && seasonId > 0) def.saleSeasonId = seasonId;
  }
  const icon = String(row.icon ?? '').trim();
  if (icon) def.icon = icon;
  const gachaBoxId = String(row.gacha_box_id ?? '').trim();
  if (gachaBoxId) def.gachaBoxId = gachaBoxId;
  // 0/음수/NaN 은 "미설정"과 같게 다룬다 — 그대로 흘리면 10연 가격이 0 이 되어 무료 뽑기가 된다.
  if (row.gacha_ten_cost != null) {
    const tenCost = Number(row.gacha_ten_cost);
    if (Number.isFinite(tenCost) && tenCost > 0) def.gachaTenCost = Math.floor(tenCost);
  }
  return def;
}

registerCategorySchema('serverGloryShopItems', GloryShopItemSchema);
